import clsx from 'clsx'
import { isEqual } from 'lodash'
import { useState } from 'react'
import { useFieldArray, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import AddIcon from '@/assets/icons/add.svg?react'
import DeleteIcon from '@/assets/icons/delete.svg?react'
import InfoCircleIcon from '@/assets/icons/info-circle.svg?react'
import Button from '@/components/Button/Button'
import ButtonIcon from '@/components/ButtonIcon/ButtonIcon'
import ConfirmModal from '@/components/ConfirmModal'
import FormField from '@/components/FormField'
import Select from '@/components/Select/Select'
import Tooltip from '@/components/Tooltip/Tooltip'
import { toast } from '@/hooks/useToast'
import type { FormFieldType } from '@/types/form-field-type'

import styles from './BasketArraySection.module.scss'
import BasketCourseArraySection from './BasketCourseArraySection'
import BasketTypeGuideModal from './BasketTypeGuideModal'
import {
  BASKET_CONFIG_BY_TYPE,
  EMPTY_BASKET
} from '../constants/curriculum-basket-config'
import type { CurriculumBasketType } from '../constants/curriculum-basket-type'
import type { CurriculumForm } from '../hooks/useCurriculumForm'
import { getCurriculumBasketTypeOptions } from '../utils/get-curriculum-basket-type-options'

const BasketArraySection = () => {
  const [basketTypeGuideModal, setBasketTypeGuideModal] = useState(false)
  const [basketRemoveModal, setBasketRemoveModal] = useState<number | null>(
    null
  )

  const { t } = useTranslation('curriculumsManagement')

  const form = useFormContext<CurriculumForm>()
  const { fields, append, remove } = useFieldArray<CurriculumForm>({
    control: form.control,
    name: 'baskets'
  })

  const createEmptyBasket = () => append(EMPTY_BASKET)
  const removeBasketClickFactory = (index: number) => () => {
    const basket = form.getValues(`baskets.${index}`)

    if (isEqual(basket, EMPTY_BASKET)) return remove(index)
    else setBasketRemoveModal(index)
  }

  const closeBasketRemoveModal = () => setBasketRemoveModal(null)
  const handleBasketRemoveModalConfirm = () => {
    if (basketRemoveModal === null) return

    remove(basketRemoveModal)
    closeBasketRemoveModal()
    toast({
      variant: 'success',
      title: t('toast.basket-removed')
    })
  }

  const openBasketTypeGuideModal = () => setBasketTypeGuideModal(true)

  const createBasketTypeChangeHandler =
    (
      basketIndex: number,
      inputProps: FormFieldType<CurriculumBasketType>
    ): FormFieldType<CurriculumBasketType>['onChange'] =>
    value => {
      inputProps.onChange?.(value)

      form.setValue(
        `baskets.${basketIndex}.courses`,
        BASKET_CONFIG_BY_TYPE[value].defaultValue
      )
    }

  return (
    <>
      <div className={styles.container}>
        {fields.length ? (
          <ul className={styles.list}>
            {fields.map((field, index) => (
              <li className={styles.listItem} key={field.id}>
                <div className={styles.header}>
                  <FormField
                    id={`baskets.${index}.type`}
                    name={`baskets.${index}.type`}
                    control={form.control}
                    required
                    label={t('label.basket-type')}
                    render={({ inputProps }) => (
                      <BasketTypeSelect
                        {...inputProps}
                        onChange={createBasketTypeChangeHandler(
                          index,
                          inputProps
                        )}
                      />
                    )}
                  />
                  <Button
                    onClick={openBasketTypeGuideModal}
                    icon={<InfoCircleIcon />}
                    variant="secondary"
                    className={clsx(styles.button, styles.guideButton)}
                  >
                    {t('button.basket-type-guide')}
                  </Button>
                  {fields.length <= 1 ? (
                    <Tooltip
                      renderTriggerAsChild
                      trigger={
                        <ButtonIcon
                          className={styles.deleteButton}
                          size="small"
                          variant="tertiary"
                          disabled
                          ariaLabel={t('button.remove-basket')}
                        >
                          <DeleteIcon />
                        </ButtonIcon>
                      }
                      text={t('tooltip.remove-basket-at-least-one')}
                    />
                  ) : (
                    <ButtonIcon
                      className={styles.deleteButton}
                      type="button"
                      size="small"
                      variant="tertiary"
                      onClick={removeBasketClickFactory(index)}
                      ariaLabel={t('button.remove-basket')}
                    >
                      <DeleteIcon />
                    </ButtonIcon>
                  )}
                </div>

                <BasketCourseArraySection basketIndex={index} />
              </li>
            ))}
          </ul>
        ) : null}
        <Button
          variant="secondary"
          onClick={createEmptyBasket}
          icon={<AddIcon />}
          className={styles.button}
        >
          {t('button.add-basket')}
        </Button>
      </div>
      <BasketTypeGuideModal
        open={basketTypeGuideModal}
        onOpenChange={setBasketTypeGuideModal}
      />
      <ConfirmModal
        open={basketRemoveModal !== null}
        onOpenChange={closeBasketRemoveModal}
        header={t('header.remove-basket')}
        subheader={
          <>
            <p>{t('help.want-to-remove-basket')}</p>
            <p>{t('help.action-cannot-be-undone')}</p>
          </>
        }
        confirmButton={
          <Button variant="danger" onClick={handleBasketRemoveModalConfirm}>
            {t('button.remove')}
          </Button>
        }
        onCancel={closeBasketRemoveModal}
      />
    </>
  )
}

const BasketTypeSelect = (props: FormFieldType<CurriculumBasketType>) => {
  const { t } = useTranslation('curriculumsManagement')
  const basketTypeOptions = getCurriculumBasketTypeOptions(t)

  return <Select options={basketTypeOptions} {...props} />
}

export default BasketArraySection
